import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EyeOff, Eye, Flag, UserX, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

function CommentRow({ c, eid, isHost, refresh }) {
  const act = async (path, body, msg) => {
    try { await api.post(`/events/${eid}/stream/${path}`, body || {}); toast.success(msg); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Something went wrong"); }
  };
  return (
    <li className={`group py-2.5 ${c.hidden ? "opacity-50" : ""}`} data-testid={`stream-comment-${c.id}`}>
      <p className="text-sm text-[#1A242B]"><span className="font-semibold">{c.name}</span> <span className="text-[#4A5D6B]">{c.text}</span></p>
      <div className="flex gap-3 mt-1 text-xs text-[#8FA89B]">
        {c.hidden && <span>Hidden</span>}
        {isHost && c.reports > 0 && <span className="text-[#B5794E]">{c.reports} report{c.reports > 1 ? "s" : ""}</span>}
        {!c.mine && !isHost && (
          <button onClick={() => act(`comments/${c.id}/report`, {}, "Thanks — the hosts will review it.")} data-testid={`stream-comment-report-${c.id}`} className="inline-flex items-center gap-1 hover:text-[#B5794E]"><Flag size={12} /> Report</button>
        )}
        {isHost && (
          <button onClick={() => act(`comments/${c.id}/hide`, { hidden: !c.hidden }, c.hidden ? "Comment shown" : "Comment hidden")} data-testid={`stream-comment-hide-${c.id}`} className="inline-flex items-center gap-1 hover:text-[#144F75]">
            {c.hidden ? <Eye size={12} /> : <EyeOff size={12} />} {c.hidden ? "Show" : "Hide"}
          </button>
        )}
        {isHost && !c.mine && (
          <button onClick={() => window.confirm(`Remove ${c.name} from this stream?`) && act(`viewers/${c.user_id}/remove`, {}, `${c.name} removed from the stream`)} data-testid={`stream-remove-viewer-${c.id}`} className="inline-flex items-center gap-1 hover:text-red-600"><UserX size={12} /> Remove viewer</button>
        )}
      </div>
    </li>
  );
}

export const StreamComments = ({ eid, isHost }) => {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const key = ["stream-comments", eid];
  const { data = [], isLoading, isError } = useQuery({ queryKey: key, queryFn: () => api.get(`/events/${eid}/stream/comments`).then((r) => r.data), refetchInterval: 5000 });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { await api.post(`/events/${eid}/stream/comments`, { text }); setText(""); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't post your comment"); }
  };
  return (
    <div className="eyw-card p-5 flex flex-col h-full" data-testid="stream-comments">
      <h2 className="font-serif text-xl text-[#1A242B] mb-2">Conversation</h2>
      <ul className="flex-1 overflow-y-auto max-h-[420px] divide-y divide-[#E5E0D8]" data-testid="stream-comments-list">
        {isLoading && <li className="py-3 text-sm text-[#8FA89B]">Loading…</li>}
        {isError && <li className="py-3 text-sm text-red-600">Comments couldn't load. They'll retry shortly.</li>}
        {!isLoading && !isError && data.length === 0 && <li className="py-3 text-sm text-[#8FA89B]" data-testid="stream-comments-empty">No comments yet — say hello.</li>}
        {data.map((c) => <CommentRow key={c.id} c={c} eid={eid} isHost={isHost} refresh={refresh} />)}
      </ul>
      <form onSubmit={send} className="mt-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Add a comment" aria-label="Add a comment"
          data-testid="stream-comment-input" className="flex-1 rounded-full border border-[#E5E0D8] bg-white px-4 py-2 text-sm focus:outline-none focus:border-[#144F75]" />
        <button type="submit" data-testid="stream-comment-send" aria-label="Send comment" className="eyw-btn !px-4 !py-2"><Send size={15} /></button>
      </form>
    </div>
  );
};
